import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import { RootState, AppDispatch } from '../store';
import { loginAsync, googleAuthAsync, clearError } from '../store/authSlice';
import { LoginRequest } from '../types';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import {
  AUTH_TESTIMONIALS,
  AUTH_SUBTITLE,
  authBgUrl,
  AUTH_BG_UNSPLASH_IDS,
} from '../config/authTestimonials';
import {
  BrandMark,
  DottedRule,
  HighlightedWord,
  INDIGO,
  INDIGO_DARK,
  INK,
  INK_SOFT,
  INTER_STACK,
  LeftPanel,
  MONO_STACK,
  SkuKicker,
} from '../components/auth/TicketTape';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const reducedMotion = useReducedMotion() ?? false;
  const { loading, error, isAuthenticated, user, tenant } = useSelector(
    (state: RootState) => state.auth,
  );

  /**
   * Post-login destination:
   *   - Seller role → /opportunities (sellers skip the wizard, their admin onboards them)
   *   - Admin role + tenant not onboarded → /completar-perfil
   *   - Admin role + tenant onboarded → /dashboard
   */
  const destinationFor = (
    role: string | undefined,
    onboardedAt: string | null | undefined,
  ): string => {
    const r = (role || '').toLowerCase();
    if (r === 'seller' || r === 'vendedormarcapropia') return '/opportunities';
    if (!onboardedAt) return '/completar-perfil';
    return '/dashboard';
  };

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
    tenantSubdomain: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  // Auto-login por query string: el bot de WhatsApp crea la cuenta con una password
  // random (que el dueño no conoce) y le manda un link `/login?email=...&password=...`.
  // Al montar, si vienen ambos, logueamos solos y redirigimos. Si solo viene email,
  // precargamos el campo sin submitear.
  const [autoLoginPending, setAutoLoginPending] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(destinationFor(user?.role, tenant?.onboardingCompletedAt));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user?.role, tenant?.onboardingCompletedAt]);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qEmail = params.get('email')?.trim();
    const qPassword = params.get('password');

    if (!qEmail) return;

    // Precargar email siempre que venga en el link (y password si también viene).
    setFormData((prev) => ({
      ...prev,
      email: qEmail,
      ...(qPassword ? { password: qPassword } : {}),
    }));

    // Sin password no hay auto-login: solo dejamos el email precargado.
    if (!qPassword) return;

    // Limpiar las credenciales de la URL para no dejarlas en el historial.
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch {
      // ignore (entornos sin History API)
    }

    setAutoLoginPending(true);
    (async () => {
      try {
        const response = await dispatch(
          loginAsync({ email: qEmail, password: qPassword, tenantSubdomain: '' }),
        ).unwrap();
        navigate(destinationFor(response.user.role, response.tenant?.onboardingCompletedAt));
      } catch {
        // Falló: caemos al form normal (el error lo muestra el slice).
        setAutoLoginPending(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password) return;
    try {
      const response = await dispatch(loginAsync(formData)).unwrap();
      navigate(destinationFor(response.user.role, response.tenant?.onboardingCompletedAt));
    } catch {
      // handled by slice
    }
  };

  const handleGoogle = async (idToken: string) => {
    try {
      const response = await dispatch(googleAuthAsync({ googleToken: idToken })).unwrap();
      navigate(destinationFor(response.user.role, response.tenant?.onboardingCompletedAt));
    } catch {
      // handled by slice
    }
  };

  const bgUrl = authBgUrl(AUTH_BG_UNSPLASH_IDS[0]);

  // MUI field styling shared between email + password: clean, Inter body,
  // 5px radius (not fully squared, not pilly), indigo focus ring.
  const fieldSx = {
    fontFamily: INTER_STACK,
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      fontFamily: INTER_STACK,
      bgcolor: '#fff',
      '& fieldset': {
        borderColor: 'rgba(15,23,42,0.18)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(15,23,42,0.32)',
      },
      '&.Mui-focused fieldset': {
        borderColor: INDIGO,
        borderWidth: 1.5,
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: INTER_STACK,
      color: INK_SOFT,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: INDIGO,
    },
  };

  // Pantalla de "Ingresando…" mientras corre el auto-login del link del bot.
  if (autoLoginPending) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          fontFamily: INTER_STACK,
          bgcolor: '#ffffff',
        }}
      >
        <CircularProgress size={36} sx={{ color: INDIGO }} />
        <Typography
          sx={{
            fontFamily: INTER_STACK,
            fontSize: 15,
            color: INK_SOFT,
          }}
        >
          Ingresando…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        fontFamily: INTER_STACK,
        bgcolor: '#ffffff',
      }}
    >
      {isDesktop && (
        <LeftPanel
          kicker="LOTE · ACCESS-042"
          headline={
            <>
              Dejá de sobrevender.
              <br />
              Dejá de contar{' '}
              <HighlightedWord>a&nbsp;mano</HighlightedWord>.
            </>
          }
          subtitle={AUTH_SUBTITLE}
          testimonials={AUTH_TESTIMONIALS}
          bgUrl={bgUrl}
          reducedMotion={reducedMotion}
        />
      )}

      <Box
        component={motion.div}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45, delay: 0.6, ease: 'easeOut' }}
        sx={{
          position: 'relative',
          flex: 1,
          width: { xs: '100%', md: '50%' },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2.5, sm: 5, md: 7, lg: 10 },
          py: { xs: 5, md: 6 },
          bgcolor: '#ffffff',
          // Faint indigo vertical grid line at inner edge (desktop only).
          '&::before': isDesktop
            ? {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '1px',
                backgroundImage: `repeating-linear-gradient(
                  to bottom,
                  ${INDIGO} 0,
                  ${INDIGO} 2px,
                  transparent 2px,
                  transparent 8px
                )`,
                opacity: 0.18,
              }
            : undefined,
        }}
      >
        {!isDesktop && (
          <Box sx={{ alignSelf: 'flex-start', mb: 4 }}>
            <BrandMark color={INK} size="md" />
          </Box>
        )}

        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* SKU kicker above headline — mirrors left panel's barcode grammar */}
          <Box sx={{ mb: 2 }}>
            <SkuKicker label="SKU · AUTH-042" />
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: INTER_STACK,
              fontSize: { xs: 26, sm: 30 },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              color: INK,
              mb: 1,
            }}
          >
            Bienvenido de <HighlightedWord>vuelta</HighlightedWord>.
          </Typography>
          <Typography
            sx={{
              fontFamily: INTER_STACK,
              fontSize: 14.5,
              color: INK_SOFT,
              mb: 3,
            }}
          >
            Ingresá para seguir con tu inventario.
          </Typography>

          {/* tiny receipt meta line */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontFamily: MONO_STACK,
              fontSize: 10.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: INK,
              opacity: 0.5,
              mb: 1.5,
            }}
          >
            <Box component="span">Doc · Login</Box>
            <Box component="span">{new Date().toISOString().slice(0, 10)}</Box>
          </Box>
          <DottedRule my={0} />

          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 2.5,
                mb: 0,
                fontFamily: INTER_STACK,
                border: '1px solid rgba(220,38,38,0.25)',
                borderRadius: '5px',
              }}
            >
              {error}
            </Alert>
          )}

          {/* Google FIRST — always above email/password */}
          <motion.div
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
          >
            <Box sx={{ mt: 2.5 }}>
              <GoogleSignInButton
                text="signin_with"
                onSuccess={handleGoogle}
                onError={(msg) => console.error(msg)}
              />
            </Box>
          </motion.div>

          <DottedRule label="o con email" my={2.5} />

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <motion.div
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.85 }}
            >
              <TextField
                fullWidth
                label="Tu email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                sx={{ mb: 2, ...fieldSx }}
              />
            </motion.div>

            <motion.div
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.95 }}
            >
              <TextField
                fullWidth
                label="Tu contraseña"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                sx={{ mb: 3, ...fieldSx }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        aria-label="Mostrar contraseña"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </motion.div>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1, mb: 2 }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  fontFamily: MONO_STACK,
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: INK_SOFT,
                  textDecoration: 'none',
                  '&:hover': { color: INDIGO },
                }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Box>

            <motion.div
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 1.05 }}
            >
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                disableElevation
                sx={{
                  py: 1.4,
                  borderRadius: '5px',
                  fontFamily: INTER_STACK,
                  fontWeight: 600,
                  fontSize: 15,
                  textTransform: 'none',
                  letterSpacing: 0,
                  bgcolor: INDIGO,
                  color: '#fff',
                  boxShadow: '0 1px 0 rgba(15,23,42,0.08)',
                  transition:
                    'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
                  '&:hover': {
                    bgcolor: INDIGO_DARK,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 16px rgba(30,64,175,0.22)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                {loading ? 'Iniciando sesión…' : 'Iniciar sesión'}
              </Button>
            </motion.div>

            <motion.div
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 1.15 }}
            >
              <Box
                sx={{
                  mt: 3,
                  height: '1px',
                  backgroundImage: `repeating-linear-gradient(to right, ${INK} 0, ${INK} 2px, transparent 2px, transparent 8px)`,
                  opacity: 0.25,
                }}
              />
              <Typography
                sx={{
                  mt: 2,
                  textAlign: 'center',
                  fontFamily: INTER_STACK,
                  fontSize: 14,
                  color: INK_SOFT,
                }}
              >
                ¿No tenés cuenta?{' '}
                <Link
                  component={RouterLink}
                  to="/register"
                  sx={{
                    fontFamily: INTER_STACK,
                    color: INDIGO,
                    fontWeight: 600,
                    textDecoration: 'none',
                    borderBottom: `1px dotted ${INDIGO}`,
                    pb: '1px',
                    '&:hover': { borderBottomStyle: 'solid' },
                  }}
                >
                  Registrate gratis →
                </Link>
              </Typography>
            </motion.div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
